import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  Autocomplete,
  Chip,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Checkbox,
  FormGroup,
  FormHelperText,
} from '@mui/material';
import eventService from '../../services/eventService';
// [magánóra-sorozat, 2026-10-31] MI VOLT A BAJ: a bővítés napjait a KLIENS számolta (`seriesExtensionDates`), és
// N darab `POST /events` hozta létre a meglévő csoport-azonosítóval (`createSeriesEvents`), részleges sikerrel —
// edzőnek ezért nem is volt elérhető (a szerver a kliens-adta csoport-azonosítót edzőtől 403-mal utasítja el).
// Most EGY `POST /events/series/{groupId}/extend`, előnézet a `dryRun`-ból (`./SeriesPreview`, ugyanaz, mint a
// létrehozóban). A `utils/recurringSeries.ts`-ből csak a hétnap-nevek és a dátum-bontás maradt.
import { DAY_NAMES_BY_GET_DAY, DAY_NAMES_MONDAY_FIRST, fromIsoDate } from '../../utils/recurringSeries';
import { dayNamesToNumbers, isSeriesOwnedBy, seriesConflictsFromError, MAX_SERIES_OCCURRENCES } from '../../utils/eventSeries';
import { SeriesPreviewAlert, SeriesConflictAlert, useSeriesPreview, seriesPreviewBlocksSave } from './SeriesPreview';
import { getTodayLocal } from '../../utils/dateUtils';
// [3. web-validátor BLOKKOLÓ] a dátummezők saját piszkozat-értékkel (a félkész gépelés nem írja vissza / át a hónapot).
import DraftDatePicker, { DraftDateStatus, draftDateError } from './DraftDatePicker';
import { useAuth } from '../../contexts/AuthContext';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useUnsavedChanges } from '../../contexts/UnsavedChangesContext';
import { isFormDirty, discardChangesConfirm } from '../../utils/formDirty';
import { requestErrorMessage, saveErrorMessage } from '../../utils/saveErrors';
import { TimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// [Web UX 9 / T2, 2026-10-13] a dátumválasztók magyar feliratai (Cancel/OK/Select date helyett) — lásd utils/dateLocale.ts
import { HU_PICKER_LOCALE_TEXT } from '../../utils/dateLocale';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { UpdateEventRequest, EventParticipantRequest, Venue, EventTypeCalendar, EventParticipant, EventSeriesConflict, ExtendEventSeriesRequest } from '../../types/calendar';
import { UserListItem, UserRole } from '../../types/user';
import { userOptionLabel } from '../../utils/userLabel';
import { calendarService } from '../../services/calendarService';
import { userService } from '../../services/userService';

/**
 * A szerkesztő ÖSSZEHASONLÍTHATÓ állapota. Külön típus, hogy a betöltéskor képzett alapvonal és
 * a jelenlegi állapot ugyanabból a formából jöjjön — így nem maradhat ki belőle mező (a
 * „Módosítás hatóköre" és a sorozat-bővítés is felhasználói bevitel).
 */
interface EventEditSnapshot {
  form: UpdateEventRequest;
  selectedUserIds: string[];
  userRoleAssignments: { [userId: string]: string };
  updateRecurringSeries: boolean;
  extendSeries: boolean;
  extendEndDate: string;
  extendIntervalWeeks: number;
  extendDayNames: string[];
}

/**
 * [1. web-validátor F1] A bővítés mintájából a szerver által MÁSOLT mezők (`EventController.cs` `ExtendEventSeries`
 * `EventSeriesPlan`: EventName, StartTime, EndTime, EventType, Venue, PlannedEventId, Description, MaxParticipants,
 * RequiredTables) + a dátum (ritmus). A résztvevőket a hívó külön veti össze.
 */
function pickExtendTemplateFields(f: UpdateEventRequest) {
  return {
    eventName: f.eventName,
    eventDate: f.eventDate,
    startTime: f.startTime,
    endTime: f.endTime,
    eventTypeId: f.eventTypeId,
    venueId: f.venueId,
    plannedEventId: f.plannedEventId,
    description: f.description,
    maxParticipants: f.maxParticipants,
    requiredTables: f.requiredTables,
  };
}

interface EventEditDialogProps {
  open: boolean;
  eventId: string | null;
  onClose: () => void;
  onEventUpdated?: () => void;
}

const EventEditDialog: React.FC<EventEditDialogProps> = ({
  open,
  eventId,
  onClose,
  onEventUpdated,
}) => {
  // [magánóra-sorozat, 2026-10-31] Sorozatot bővíthet: az admin mindig, az edző a SAJÁT sorozatán
  // (`seriesOwnedByMe`, lent). Korábban csak admin — a szerver akkor az edző sorozat-létrehozását 403-mal utasította
  // el. A ritmus-horgonyt ma a SZERVER adja (a sorozat legkorábbi aktív tagja), nem a kliens által látott lista.
  const { user } = useAuth();
  const { showSuccess, showError } = useFeedback();
  const confirm = useConfirm();
  const isAdmin = user?.userRole === 'Administrator';
  const isCoach = user?.userRole === 'Coach';
  const [formData, setFormData] = useState<UpdateEventRequest>({
    eventName: '',
    eventDate: '',
    startTime: '',
    endTime: '',
    eventTypeId: '',
    venueId: '',
    description: '',
    isActive: true,
    maxParticipants: undefined,
    requiredTables: 1,
    participants: [],
  });

  const [venues, setVenues] = useState<Venue[]>([]);
  const [eventTypes, setEventTypes] = useState<EventTypeCalendar[]>([]);
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<UserListItem[]>([]);
  const [userRoleAssignments, setUserRoleAssignments] = useState<{[userId: string]: string}>({});
  const [currentParticipants, setCurrentParticipants] = useState<EventParticipant[]>([]);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState<string>('');
  /**
   * [Web 7 / T3 javító kör 1, 2026-10-06] A BETÖLTÖTT esemény már TÖRÖLT (`isActive: false` a
   * `GET /events/{id}`-ben — a törlés puha, a végpont kiadja). Ilyenkor a szerkesztés értelmetlen: a
   * szerver a PUT-ot 409-cel utasítja el. Ezért MEGNYITÁSKOR jól látható figyelmeztetés, és a Mentés
   * tiltva (a mobil mintája: `mobile/app/events/[id]/edit.tsx` `deletedOnServer`). Csak a kifejezett
   * `false` számít.
   */
  const [deletedOnServer, setDeletedOnServer] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  const [recurringEventGroupId, setRecurringEventGroupId] = useState<string | null>(null);
  const [updateRecurringSeries, setUpdateRecurringSeries] = useState<boolean>(false);

  // Sorozat bővítése — csak HOZZÁADÁS: új végdátum / napok / gyakoriság alapján a még nem létező
  // (és nem múltbeli) napokra jön létre esemény, a sorozat első napjához igazított ritmusban.
  // Törlés nincs: a meglévő alkalmakon már lehet jelenléti ív és kifizetés. [klub-döntés 2026-09-11]
  const [seriesDates, setSeriesDates] = useState<string[]>([]); // a sorozat létező napjai (YYYY-MM-DD)
  const [extendSeries, setExtendSeries] = useState(false);
  const [extendEndDate, setExtendEndDate] = useState<string>('');
  const [extendIntervalWeeks, setExtendIntervalWeeks] = useState<number>(1);
  const [extendDayNames, setExtendDayNames] = useState<string[]>([]);
  /**
   * [magánóra-sorozat, 2026-10-31] „Saját sorozat": a `GET /events?recurringEventGroupId=` MINDEN (látható) tagjának
   * `createdBy`-ja a bejelentkezett edző (`utils/eventSeries.ts` `isSeriesOwnedBy`). Csak a bővítő blokk
   * megjelenítéséről dönt — a szerver a mérvadó (ő az inaktív és a nem látható tagokat is nézi, 403).
   */
  const [seriesOwnedByMe, setSeriesOwnedByMe] = useState(false);
  /** [3. web-validátor] A dátum-piszkozatok állapota (`DraftDatePicker`): érvénytelen / üres → nincs mentés. */
  const [dateDraft, setDateDraft] = useState<{ eventDate?: DraftDateStatus; extendEndDate?: DraftDateStatus }>({});
  /** A bővítés 400-as ütközés-válaszának napjai (minden-vagy-semmi: semmi nem jött létre). */
  const [extendConflicts, setExtendConflicts] = useState<EventSeriesConflict[]>([]);
  /**
   * A BETÖLTÖTT esemény, mint alapvonal a mentetlen-változás védelemhez: a szerkesztő minden
   * felhasználói állapota (űrlap, résztvevők + szerepkörök, módosítás hatóköre, sorozat-bővítés)
   * egyetlen összehasonlítható pillanatképben. `null` = még nem töltött be → soha nem piszkos.
   */
  const [baseline, setBaseline] = useState<EventEditSnapshot | null>(null);

  /** Bővíthet: admin mindig, edző a saját sorozatán (a szerver a mérvadó). */
  const canExtendSeries = !!recurringEventGroupId && (isAdmin || (isCoach && seriesOwnedByMe));

  /**
   * [magánóra-sorozat, 2026-10-31, 1. web-validátor F1] MI VOLT A BAJ: a szerver a bővítés mintáját (`templateEventId`)
   * az ADATBÁZISBÓL olvassa (`EventController.cs` `ExtendEventSeries`: név, kezdés/befejezés, típus, helyszín, sablon,
   * leírás, létszám, asztalszám, résztvevők), tehát az előnézet a MENTETT eseménnyel számol. Ha ezek közül valamit az
   * űrlapon átírtak, az előnézet félrevezető (tiltott, pedig a javított idővel nem ütközne — vagy engedte, és a PUT után
   * bukott). DÖNTÉS (koordinátor): ilyenkor a bővítés KI van kapcsolva, és a felhasználó előbb ment, utána bővít.
   * A dátum is benne van (a ritmus és az ütközés a mentett alkalmakkal számol).
   */
  const extendSourceDirty = !!baseline && isFormDirty(
    {
      ...pickExtendTemplateFields(baseline.form),
      selectedUserIds: baseline.selectedUserIds,
      userRoleAssignments: baseline.userRoleAssignments,
    },
    {
      ...pickExtendTemplateFields(formData),
      selectedUserIds: selectedUsers.map(u => u.userId),
      userRoleAssignments,
    },
  );
  /** A bővítés ténylegesen él: bejelölve ÉS a minta-mezők mentettek. */
  const extendActive = extendSeries && canExtendSeries && !extendSourceDirty;
  useEffect(() => {
    // A bejelölt bővítés a minta-mezők módosításakor KIKAPCSOL (és így a mentést sem tiltja az előnézete).
    if (extendSeries && extendSourceDirty) setExtendSeries(false);
  }, [extendSeries, extendSourceDirty]);

  useEffect(() => {
    if (open && eventId) {
      loadAllData();
    }
  }, [open, eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAllData = async () => {
    if (!eventId) return;

    try {
      setInitialLoading(true);
      setError('');
      setDeletedOnServer(false);
      // Betöltés közben nincs mentetlen adat (és az előző esemény alapvonala már nem érvényes).
      setBaseline(null);

      // Load static data first, then event data
      const [venuesData, eventTypesData, usersResult, rolesData, details, participants] = await Promise.all([
        calendarService.getActiveVenues(),
        calendarService.getActiveEventTypes(),
        userService.getUsers(),
        userService.getUserRoles(),
        calendarService.getEventById(eventId),
        calendarService.getEventParticipants(eventId),
      ]);

      // Set static data first
      const activeUsers = usersResult.data.filter(user => user.isActive);
      setVenues(venuesData);
      setEventTypes(eventTypesData);
      setUsers(activeUsers);
      setUserRoles(rolesData.filter(role => role.isActive && role.name !== 'Administrator'));

      // Then set current participants and form data
      // A kijelölés NULLÁZÁSA azért kell, mert az alatta lévő effekt csak akkor tölti fel, ha
      // van résztvevő — nélküle egy résztvevő NÉLKÜLI esemény megnyitásakor az ELŐZŐ esemény
      // résztvevői maradtak kijelölve (és a mentés őket írta volna be).
      setSelectedUsers([]);
      setUserRoleAssignments({});
      setCurrentParticipants(participants);

      // Set recurring event group ID if exists
      console.log('EventEditDialog: Received event details:', details);
      console.log('EventEditDialog: recurringEventGroupId from API:', details.recurringEventGroupId);
      setRecurringEventGroupId(details.recurringEventGroupId || null);
      console.log('EventEditDialog: Set recurringEventGroupId state to:', details.recurringEventGroupId || null);
      setUpdateRecurringSeries(false); // Reset to default (only this event)

      // Sorozat-bővítés alaphelyzetbe; az alapértelmezett nap ennek az eseménynek a hétköznapja.
      setExtendSeries(false);
      setExtendEndDate('');
      setExtendIntervalWeeks(1);
      setExtendDayNames([DAY_NAMES_BY_GET_DAY[fromIsoDate(details.eventDate.split('T')[0]).getDay()]]);
      setExtendConflicts([]);
      setSeriesOwnedByMe(false);
      if (details.recurringEventGroupId) {
        try {
          const series = await eventService.getEvents({ recurringEventGroupId: details.recurringEventGroupId });
          setSeriesDates(series.map(e => String(e.eventDate).split('T')[0]).sort());
          setSeriesOwnedByMe(isSeriesOwnedBy(series, user?.userId));
        } catch (seriesErr) {
          console.error('Failed to load the recurring series:', seriesErr);
          setSeriesDates([details.eventDate.split('T')[0]]);
          // Ismeretlen tulajdon → edzőnek nem mutatjuk a bővítést (a szerver úgyis eldöntené).
          setSeriesOwnedByMe(false);
        }
      } else {
        setSeriesDates([]);
      }

      // Initialize form data - now venues and eventTypes are already loaded
      const loadedForm: UpdateEventRequest = {
        eventName: details.eventName,
        eventDate: details.eventDate,
        startTime: details.startTime,
        endTime: details.endTime,
        eventTypeId: details.eventTypeId,
        venueId: details.venueId,
        description: details.description || '',
        isActive: details.isActive,
        maxParticipants: details.maxParticipants,
        // `??`, nem `||` — de a korábbi INDOKLÁS TÉVES VOLT, ezért itt a MÉRT tények [N-1,
        // 2026-09-14]. A komment azt állította, hogy az élő `/api/events` „VALÓBAN ad
        // `requiredTables: 0`-t (asztalt nem igénylő esemény)". A MÉRÉS: mind a 110 élő esemény
        // RÉSZLETEZŐ végpontján (`getEventById` — ezt olvassa ez a dialógus) az érték
        // {1:72, 2:20, 3:1, 5:12, 10:3, 13:2}, azaz 0 vagy null: NULLA darab. Az „asztalt nem
        // igénylő esemény" nem is létező fogalom: az `UpdateEventDto.RequiredTables`
        // `[Range(1, int.MaxValue)]`, tehát a `requiredTables: 0` MENTÉSE 400-at ad.
        // A `?? 1` mégis MARAD: ártalmatlan (csak a hiányzó/null értéket pótolja), és ha a
        // lista-végpont mapping-hiánya miatt egyszer mégis 0 jönne be, a `||`-os változat NÉMÁN
        // 1-re írná át — ami mentésnél a 0-t „javítaná", a `??` pedig legalább megtartja.
        requiredTables: details.requiredTables ?? 1,
        participants: [],
        // A sablon-azonosító megőrzése: a backend feltétel nélkül átírja az UpdateEventDto értékére,
        // ezért ha nem küldjük, a mentés nullázná — és az esemény kiesne az egyenleg „tervezett" számításából.
        plannedEventId: details.plannedEventId,
      };
      setFormData(loadedForm);
      setDeletedOnServer(details.isActive === false);

      // Az ALAPVONAL a szerver adataiból, ugyanazzal a leképezéssel, amit az alatta lévő effekt
      // is használ (kijelölt résztvevő = aktív felhasználók között megtalált; szerepkör-hozzárendelés
      // MINDEN résztvevőre). Így a puszta betöltés nem tud „mentetlen módosítást" előállítani.
      setBaseline({
        form: loadedForm,
        selectedUserIds: participants
          .map(p => p.userId)
          .filter(id => activeUsers.some(u => u.userId === id)),
        userRoleAssignments: participants.reduce<{ [userId: string]: string }>((acc, p) => {
          acc[p.userId] = p.userRoleId;
          return acc;
        }, {}),
        updateRecurringSeries: false,
        extendSeries: false,
        extendEndDate: '',
        extendIntervalWeeks: 1,
        extendDayNames: [DAY_NAMES_BY_GET_DAY[fromIsoDate(details.eventDate.split('T')[0]).getDay()]],
      });

    } catch (err: any) {
      setError(requestErrorMessage(err, 'Hiba történt az adatok betöltésekor'));
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    if (currentParticipants.length > 0 && users.length > 0) {
      // Initialize selected users and role assignments based on current participants
      const participantUsers = currentParticipants.map(p => 
        users.find(u => u.userId === p.userId)
      ).filter(Boolean) as UserListItem[];
      
      setSelectedUsers(participantUsers);

      const roleAssignments: {[userId: string]: string} = {};
      currentParticipants.forEach(p => {
        roleAssignments[p.userId] = p.userRoleId;
      });
      setUserRoleAssignments(roleAssignments);
    }
  }, [currentParticipants, users]);

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};

    if (!formData.eventName.trim()) {
      errors.eventName = 'Az esemény neve kötelező';
    }

    if (!formData.eventTypeId) {
      errors.eventTypeId = 'Az esemény típusa kötelező';
    }

    if (!formData.venueId) {
      errors.venueId = 'A helyszín kiválasztása kötelező';
    }

    const startTime = new Date(`2000-01-01T${formData.startTime}`);
    const endTime = new Date(`2000-01-01T${formData.endTime}`);
    
    if (startTime >= endTime) {
      errors.endTime = 'A befejezés időpontja későbbi kell legyen a kezdésnél';
    }

    if (selectedUsers.length === 0) {
      errors.participants = 'Legalább egy résztvevőt ki kell választani';
    } else {
      // Check if all selected users have assigned roles
      const missingRoles = selectedUsers.filter(user => !userRoleAssignments[user.userId]);
      if (missingRoles.length > 0) {
        errors.participants = 'Minden résztvevőhöz szerepkört kell rendelni';
      }
    }

    // [3. web-validátor] a „Dátum" piszkozata: érvénytelen vagy üres → nincs mentés (nem a régi értékkel megy ki).
    const eventDateDraftError = draftDateError(dateDraft.eventDate, true);
    if (eventDateDraftError) {
      errors.eventDate = eventDateDraftError;
    }

    if (extendActive) {
      const untilDraftError = draftDateError(dateDraft.extendEndDate, false);
      if (untilDraftError) {
        errors.extendEndDate = untilDraftError;
      } else if (!extendEndDate) {
        errors.extendEndDate = 'A bővítés végdátuma kötelező';
      } else if (extendEndDate <= getTodayLocal()) {
        errors.extendEndDate = 'A végdátum a mai napnál későbbi kell legyen';
      }
      if (extendDayNames.length === 0) {
        errors.extendDayNames = 'Legalább egy napot ki kell választani';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * [magánóra-sorozat, 2026-10-31] A bővítés kérése EGY helyen (előnézet és mentés ugyanazt küldi). A ritmust a
   * SZERVER igazítja a sorozat legkorábbi aktív tagjához, a létező és a ma előtti napokat ő hagyja ki — a kliens
   * nem számol napot. Minta-alkalom (`templateEventId`) = ez az esemény.
   */
  const extendRequest: Omit<ExtendEventSeriesRequest, 'dryRun'> | null =
    open && eventId && extendActive && extendEndDate && extendEndDate > getTodayLocal() && extendDayNames.length > 0
      && (!dateDraft.extendEndDate || dateDraft.extendEndDate === 'valid')
      ? {
          templateEventId: eventId,
          repeatUntil: extendEndDate,
          intervalWeeks: extendIntervalWeeks,
          daysOfWeek: dayNamesToNumbers(extendDayNames),
        }
      : null;
  const extendPreview = useSeriesPreview(extendRequest, (req) =>
    calendarService.extendSeries(recurringEventGroupId as string, { ...req, dryRun: true }));
  const saveBlockedByExtend = extendActive && seriesPreviewBlocksSave(extendPreview);
  // [1. web-validátor M2] A bővítés 400-as ütközés-listája elavul, amint a bővítés vagy a minta bármely mezője változik.
  const extendConflictKey = JSON.stringify({ extendRequest, extendSeries, form: pickExtendTemplateFields(formData), ids: selectedUsers.map(u => u.userId), userRoleAssignments });
  useEffect(() => {
    setExtendConflicts([]);
  }, [extendConflictKey]);

  const currentSnapshot: EventEditSnapshot = {
    form: formData,
    selectedUserIds: selectedUsers.map(u => u.userId),
    userRoleAssignments,
    updateRecurringSeries,
    extendSeries,
    extendEndDate,
    extendIntervalWeeks,
    extendDayNames,
  };
  // `open &&`: a dialógus zárva is mountolva marad a szülőben az utolsó tartalommal.
  const isDirty = open && isFormDirty(baseline, currentSnapshot);
  useUnsavedChanges(isDirty, 'Esemény szerkesztése');

  /** A Dialog `onClose`-a (backdrop + ESC) és a „Mégsem" gomb közös, védett záró útja. */
  const handleClose = async () => {
    if (isDirty && !(await confirm(discardChangesConfirm('esemény szerkesztője')))) {
      return;
    }
    onClose();
  };

  const handleSubmit = async () => {
    // [Web 7 / T3 javító kör 1] törölt eseményre nincs mentés (a gomb is tiltva — ez a második zár).
    if (deletedOnServer) return;
    if (!validateForm() || !eventId) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const participants: EventParticipantRequest[] = selectedUsers.map(user => ({
        userId: user.userId,
        userRoleId: userRoleAssignments[user.userId],
      }));

      const eventData: UpdateEventRequest = {
        ...formData,
        participants,
        updateRecurringSeries: updateRecurringSeries,
      };

      await calendarService.updateEvent(eventId, eventData);

      // Sikeres mentés visszajelzése: a szöveg tükrözze a "Módosítás hatóköre" választást,
      // hogy a felhasználó lássa, csak ezt az egy alkalmat, vagy a sorozat többi tagját is érintette.
      const scopeMessage =
        recurringEventGroupId && updateRecurringSeries
          ? 'A sorozat további alkalmai módosítva.'
          : 'Az esemény módosítva.';
      let extendedCount = 0;

      // [magánóra-sorozat, 2026-10-31] Sorozat bővítése: PONTOSAN EGY `POST /events/series/{groupId}/extend`. Az új
      // alkalmak a (most mentett) minta-alkalom adataival jönnek létre; ütközésnél a szerver SEMMIT nem hoz létre.
      setExtendConflicts([]);
      if (extendActive && recurringEventGroupId) {
        if (!extendRequest) {
          setBaseline(currentSnapshot);
          onEventUpdated?.();
          setError('A bővítés beállításai hiányosak — az esemény módosítása mentve, új alkalom nem jött létre.');
          return;
        }
        try {
          const extended = await calendarService.extendSeries(recurringEventGroupId, { ...extendRequest, dryRun: false });
          extendedCount = extended.created.length;
        } catch (extendErr: any) {
          // Az ESEMÉNY MENTÉSE MÁR MEGTÖRTÉNT (csak a bővítés bukott, és a szerver minden-vagy-semmi: egy új alkalom
          // sem jött létre). A bezárás így nem veszít el mentetlen adatot — az alapvonal a jelenlegi állapot.
          setBaseline(currentSnapshot);
          onEventUpdated?.();
          setExtendConflicts(seriesConflictsFromError(extendErr));
          setError(saveErrorMessage(extendErr, 'Az esemény módosítása mentve, de a sorozat bővítése nem sikerült'));
          return;
        }
      }

      showSuccess(
        extendedCount > 0
          ? `${scopeMessage} ${extendedCount} új alkalom hozzáadva a sorozathoz.`
          : scopeMessage
      );
      onEventUpdated?.();
      onClose();
    } catch (err: any) {
      // [Web 7 / T3, 2026-10-06] Az eseményt a szerkesztő NYITVA tartása alatt TÖRÖLTÉK (lelet: handoff 4e,
      // „törölt esemény/elérhetőség PUT-tal visszaírható"). A backend ilyenkor 409-et ad a saját magyar
      // mondatával, és semmit nem ír (`EventController.cs` `UpdateEvent`, „TÖRÖLT esemény nem írható vissza").
      // A módosítás tehát NEM menthető: a szerkesztőt KÉRDÉS NÉLKÜL zárjuk (a mentetlen-védelem itt hazudna —
      // nincs mit megmenteni), a szerver mondata toastban látszik, és a szülő naptára újratölt
      // (`onEventUpdated`), hogy a törölt esemény eltűnjön. A `PUT /events/{id}` más 409-et nem ad.
      if (err?.response?.status === 409) {
        showError(saveErrorMessage(err, 'Ezt az eseményt időközben törölték, a módosítás nem menthető.'));
        onEventUpdated?.();
        onClose();
        return;
      }
      // [validátor M-1, 2026-10-03] 401-nél (újra-belépő ablak nyitva) ugyanaz a mondat, mint az
      // `EventCreateDialog`-ban — a régi általános szöveg ellentmondott az ablaknak.
      // [3. javító kör, F-1] a közös, zár-tudatos burkolón át (`utils/saveErrors.ts`).
      setError(saveErrorMessage(err, 'Hiba történt az esemény frissítésekor'));
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (users: UserListItem[]) => {
    setSelectedUsers(users);

    // Remove role assignments for users that are no longer selected
    const newRoleAssignments = { ...userRoleAssignments };
    Object.keys(newRoleAssignments).forEach(userId => {
      if (!users.find(user => user.userId === userId)) {
        delete newRoleAssignments[userId];
      }
    });

    // Auto-assign role based on user's system role for newly added users
    users.forEach(user => {
      if (!newRoleAssignments[user.userId]) {
        const matchingRole = userRoles.find(role => role.name === user.userRole);
        if (matchingRole) {
          newRoleAssignments[user.userId] = matchingRole.userRoleId;
        }
      }
    });

    setUserRoleAssignments(newRoleAssignments);
  };

  const handleUserRoleChange = (userId: string, roleId: string) => {
    setUserRoleAssignments(prev => ({
      ...prev,
      [userId]: roleId,
    }));
  };

  const parseTimeString = (timeString: string) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return dayjs().set('hour', hours).set('minute', minutes).set('second', 0);
  };

  const formatTimeString = (date: dayjs.Dayjs): string => {
    return date.format('HH:mm:ss');
  };

  if (initialLoading) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    // [Web UX 9 / T2, 2026-10-13] `adapterLocale="hu"` PÓTOLVA: nélküle az AdapterDayjs KÉNYSZERÍTETTEN angol
    // (`getCurrentLocaleCode: this.locale || 'en'`) — angol hónapnevek („October 2026"), vasárnapos hét, amerikai MM/DD/YYYY mező.
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="hu" localeText={HU_PICKER_LOCALE_TEXT}>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Esemény szerkesztése
        </DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {extendConflicts.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <SeriesConflictAlert conflicts={extendConflicts} testId="series-extend-submit-conflicts" />
            </Box>
          )}
          {deletedOnServer && (
            <Alert severity="warning" sx={{ mb: 2 }} data-testid="event-edit-deleted-alert">
              Ezt az eseményt időközben törölték. A módosítás nem menthető.
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {/* Event Name */}
            <TextField
              label="Esemény neve"
              value={formData.eventName}
              onChange={(e) => setFormData(prev => ({ ...prev, eventName: e.target.value }))}
              error={!!validationErrors.eventName}
              helperText={validationErrors.eventName}
              fullWidth
              required
            />

            {/* Recurring Event Update Options */}
            {recurringEventGroupId && (
              <FormControl component="fieldset">
                <FormLabel component="legend">Módosítás hatóköre</FormLabel>
                <RadioGroup
                  value={updateRecurringSeries ? 'all' : 'single'}
                  onChange={(e) => setUpdateRecurringSeries(e.target.value === 'all')}
                >
                  <FormControlLabel
                    value="single"
                    control={<Radio />}
                    label="Csak ez az esemény"
                  />
                  <FormControlLabel
                    value="all"
                    control={<Radio />}
                    label="Összes ismétlődő esemény"
                  />
                </RadioGroup>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  {updateRecurringSeries
                    ? 'A módosítások az összes ismétlődő eseményre érvényesek lesznek (az esemény dátuma kivételével).'
                    : 'A módosítások csak erre az egy eseményre lesznek érvényesek.'}
                </Typography>
              </FormControl>
            )}

            {/* Sorozat bővítése — csak hozzáadás; admin mindig, edző a saját sorozatán [magánóra-sorozat, 2026-10-31] */}
            {canExtendSeries && (
              <Box sx={{ p: 2, backgroundColor: 'action.hover', borderRadius: 1, display: 'flex', flexDirection: 'column', gap: 2 }} data-testid="series-extension">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={extendSeries}
                      onChange={(e) => setExtendSeries(e.target.checked)}
                      disabled={loading || extendSourceDirty}
                      inputProps={{ 'data-testid': 'series-extend-checkbox' } as any}
                    />
                  }
                  label={`További alkalmak hozzáadása a sorozathoz (jelenleg ${seriesDates.length} alkalom${seriesDates.length > 0 ? `, ${seriesDates[0]} – ${seriesDates[seriesDates.length - 1]}` : ''})`}
                />
                {extendSourceDirty && (
                  <Alert severity="info" data-testid="series-extend-save-first">
                    Előbb mentsd a módosításokat, utána bővítsd a sorozatot — a bővítés a mentett adatokkal számol.
                  </Alert>
                )}
                {extendActive && (
                  <>
                    <DraftDatePicker
                      label="Bővítés végdátuma"
                      value={extendEndDate}
                      syncKey={`${open}-${eventId}`}
                      onDraftStatusChange={(status) => setDateDraft(prev => ({ ...prev, extendEndDate: status }))}
                      onChange={(until) => {
                        if (until) {
                          setExtendEndDate(until);
                          setValidationErrors(prev => { const n = { ...prev }; delete n.extendEndDate; return n; });
                        }
                      }}
                      minDate={dayjs(getTodayLocal()).add(1, 'day')}
                      disabled={loading}
                      slotProps={{
                        textField: {
                          required: true,
                          error: !!validationErrors.extendEndDate,
                          helperText: validationErrors.extendEndDate || 'Eddig a napig jönnek létre az új alkalmak (a meglévők maradnak)',
                          inputProps: { 'data-testid': 'series-extend-end-date' },
                        },
                      }}
                    />
                    <FormControl fullWidth disabled={loading}>
                      <InputLabel id="series-extend-interval-label">Ismétlődés gyakorisága</InputLabel>
                      <Select
                        labelId="series-extend-interval-label"
                        label="Ismétlődés gyakorisága"
                        value={extendIntervalWeeks}
                        onChange={(e) => setExtendIntervalWeeks(Number(e.target.value))}
                        inputProps={{ 'data-testid': 'series-extend-interval' }}
                      >
                        <MenuItem value={1}>Minden héten</MenuItem>
                        <MenuItem value={2}>Kéthetente</MenuItem>
                        <MenuItem value={3}>Háromhetente</MenuItem>
                        <MenuItem value={4}>Négyhetente</MenuItem>
                      </Select>
                      <FormHelperText>A hetek a sorozat első alkalmának hetétől számolódnak.</FormHelperText>
                    </FormControl>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>A hét napjai:</Typography>
                      <FormGroup row>
                        {DAY_NAMES_MONDAY_FIRST.map((day) => (
                          <FormControlLabel
                            key={day}
                            control={
                              <Checkbox
                                checked={extendDayNames.includes(day)}
                                onChange={(e) => {
                                  setExtendDayNames(prev => e.target.checked ? [...prev, day] : prev.filter(d => d !== day));
                                  setValidationErrors(prev => { const n = { ...prev }; delete n.extendDayNames; return n; });
                                }}
                                disabled={loading}
                                inputProps={{ 'data-testid': `series-extend-day-${day}` } as any}
                              />
                            }
                            label={day}
                          />
                        ))}
                      </FormGroup>
                      {validationErrors.extendDayNames && (
                        <FormHelperText error>{validationErrors.extendDayNames}</FormHelperText>
                      )}
                    </Box>
                    {/* [magánóra-sorozat, 2026-10-31] Előnézet a szerver `dryRun`-jából (közös komponens a létrehozóval). */}
                    <Typography variant="caption" color="text.secondary">
                      Legfeljebb {MAX_SERIES_OCCURRENCES} új alkalom egy bővítéssel. Ha bármelyik új nap ütközik, egy alkalom sem jön létre.
                    </Typography>
                    <SeriesPreviewAlert
                      state={extendPreview}
                      testId="series-extend-preview"
                      existingCount={seriesDates.length}
                      idleHint="Az előnézethez add meg a bővítés végdátumát (a mai napnál későbbi) és legalább egy napot."
                      blockedHint="Ha nem akarsz bővíteni, kapcsold ki a sorozat bővítését."
                    />
                    <Typography variant="caption" color="text.secondary">
                      Az új alkalmak ENNEK az eseménynek a MENTETT adataival jönnek létre (név, időpont, típus, helyszín, asztalszám,
                      létszám, résztvevők). Ha ezeken változtatnál, előbb mentsd az eseményt, utána bővíts — amíg az űrlapon mentetlen
                      módosítás van, a bővítés ki van kapcsolva. Ha ez az alkalom egyedileg el volt térítve, előbb állítsd vissza.
                      Alkalom törlése itt nem lehetséges — egy alkalmat, vagy a sorozatot innentől, az esemény ablakában lehet törölni.
                    </Typography>
                  </>
                )}
              </Box>
            )}

            {/* Date and Time */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <DraftDatePicker
                label="Dátum"
                value={String(formData.eventDate || '').slice(0, 10)}
                syncKey={`${open}-${eventId}`}
                onDraftStatusChange={(status) => {
                  setDateDraft(prev => ({ ...prev, eventDate: status }));
                  if (status === 'valid') setValidationErrors(prev => { const n = { ...prev }; delete n.eventDate; return n; });
                }}
                onChange={(newDate) => setFormData(prev => ({ ...prev, eventDate: newDate }))}
                slotProps={{ textField: { error: !!validationErrors.eventDate, helperText: validationErrors.eventDate } }}
                sx={{ flex: 1 }}
              />
              <TimePicker
                label="Kezdés"
                value={parseTimeString(formData.startTime)}
                onChange={(time) => {
                  if (time && dayjs.isDayjs(time)) {
                    setFormData(prev => ({ ...prev, startTime: formatTimeString(time) }));
                  }
                }}
                ampm={false}
                sx={{ flex: 1 }}
              />
              <TimePicker
                label="Befejezés"
                value={parseTimeString(formData.endTime)}
                onChange={(time) => {
                  if (time && dayjs.isDayjs(time)) {
                    setFormData(prev => ({ ...prev, endTime: formatTimeString(time) }));
                  }
                }}
                ampm={false}
                slotProps={{
                  textField: {
                    error: !!validationErrors.endTime
                  }
                }}
                sx={{ flex: 1 }}
              />
            </Box>
            {validationErrors.endTime && (
              <Typography variant="caption" color="error" sx={{ mt: -1 }}>
                {validationErrors.endTime}
              </Typography>
            )}

            {/* Event Type and Venue */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth error={!!validationErrors.eventTypeId}>
                <InputLabel>Esemény típusa *</InputLabel>
                <Select
                  value={formData.eventTypeId}
                  label="Esemény típusa *"
                  onChange={(e) => setFormData(prev => ({ ...prev, eventTypeId: e.target.value }))}
                >
                  {eventTypes.map((type) => (
                    <MenuItem key={type.eventTypeId} value={type.eventTypeId}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            backgroundColor: type.color,
                            borderRadius: '50%',
                          }}
                        />
                        {type.displayName}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.eventTypeId && (
                  <Typography variant="caption" color="error">
                    {validationErrors.eventTypeId}
                  </Typography>
                )}
              </FormControl>

              <FormControl fullWidth error={!!validationErrors.venueId}>
                <InputLabel>Helyszín *</InputLabel>
                <Select
                  value={formData.venueId}
                  label="Helyszín *"
                  onChange={(e) => setFormData(prev => ({ ...prev, venueId: e.target.value }))}
                >
                  {venues.map((venue) => (
                    <MenuItem key={venue.venueId} value={venue.venueId}>
                      {venue.name}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.venueId && (
                  <Typography variant="caption" color="error">
                    {validationErrors.venueId}
                  </Typography>
                )}
              </FormControl>
            </Box>

            {/* Description */}
            <TextField
              label="Leírás"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              multiline
              minRows={1}
              maxRows={4}
              fullWidth
              sx={{
                '& .MuiInputBase-root': {
                  maxHeight: '120px',
                  overflow: 'auto',
                },
                '& .MuiInputBase-inputMultiline': {
                  resize: 'vertical',
                  minHeight: '20px',
                }
              }}
            />

            {/* Maximum Participants and Required Tables */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Maximum résztvevők száma"
                type="number"
                value={formData.maxParticipants ?? ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? undefined : parseInt(e.target.value, 10);
                  setFormData(prev => ({ ...prev, maxParticipants: Number.isNaN(value) ? undefined : value }));
                }}
                helperText="Opcionális – hagyja üresen korlátlan résztvevéshez"
                fullWidth
                inputProps={{ min: 1, max: 99 }}
              />
              <TextField
                label="Szükséges asztalok száma"
                type="number"
                value={formData.requiredTables ?? ''}
                onChange={(e) => {
                  const parsed = parseInt(e.target.value, 10);
                  setFormData(prev => ({ ...prev, requiredTables: Number.isNaN(parsed) ? 1 : parsed }));
                }}
                fullWidth
                inputProps={{ min: 1, max: 99 }}
              />
            </Box>

            {/* Participants */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Résztvevők *
              </Typography>
              {/* [C10, 2026-10-30] MI VOLT A BAJ: a címke `${user.name} (${user.email})` volt — a szerver
                    edzőnek MÁS tag e-mailjét `null`-ként adja (`backend/PinyoApi/Services/UserListSanitizer.cs`),
                    így „Név (null)" lett volna. `userOptionLabel`: e-mail nélkül csak a név (admin: változatlan).
                    A sor-kulcs és az azonosság a `userId`-n áll, NEM a címkén/e-mailen (azonos nevű tagok). */}
              <Autocomplete
                multiple
                options={users}
                getOptionLabel={(user) => userOptionLabel(user)}
                isOptionEqualToValue={(a, b) => a.userId === b.userId}
                renderOption={(props, user) => {
                  const { key, ...optionProps } = props as any;
                  return (
                    <li key={user.userId} {...optionProps}>
                      {userOptionLabel(user)}
                    </li>
                  );
                }}
                value={selectedUsers}
                onChange={(_, newUsers) => handleUserSelect(newUsers)}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      label={option.name}
                      {...getTagProps({ index })}
                      key={option.userId}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Válasszon résztvevőket"
                    error={!!validationErrors.participants}
                    helperText={validationErrors.participants}
                  />
                )}
              />

              {/* Role assignments for selected users */}
              {selectedUsers.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Szerepkör hozzárendelés:
                  </Typography>
                  {selectedUsers.map((user) => (
                    <Box key={user.userId} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <Typography variant="body2" sx={{ minWidth: 200 }}>
                        {user.name}
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Szerepkör</InputLabel>
                        <Select
                          value={userRoleAssignments[user.userId] || ''}
                          label="Szerepkör"
                          onChange={(e) => handleUserRoleChange(user.userId, e.target.value)}
                        >
                          {userRoles.map((role) => (
                            <MenuItem key={role.userRoleId} value={role.userRoleId}>
                              {role.displayName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            Mégsem
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            // [magánóra-sorozat, 2026-10-31] Ütköző / érvénytelen bővítés-előnézetnél a mentés tiltva.
            disabled={loading || deletedOnServer || saveBlockedByExtend}
          >
            {loading ? 'Mentés...' : 'Mentés'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default EventEditDialog;